/*
 * Security controls (docs/api.md, "Security controls"; rules in
 * docs/security-controls.md): who can reach an environment, how logins are
 * protected, bots and rate limits, firewall tuning and response headers,
 * plus the overview, what was blocked and who logged in, copying, saved
 * address lists and the security profiles' shapes (api/securityProfiles.ts).
 *
 * Every change route takes `dryRun: true`, which saves nothing and says what
 * would change and what it does to the caller's own address; the review
 * step uses it before applying. Until a route answers (a 404 while the API
 * is being built), screens say so and keep milestone 4's settings working.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrg } from "../app/org";
import { api, isApiError } from "./client";
import { keys } from "./keys";
import { watchOperation } from "./live";

/* ── Shapes (docs/api.md) ───────────────────────────────────────── */

export type Part = "access" | "login" | "bots" | "rateLimit" | "firewall" | "headers" | "rules";
export type SecurityScope = "site" | "login";
export type ExpiresIn = "1h" | "24h" | "7d" | "30d" | null;
export type BlockReason = "ip" | "rule" | "only-ips" | "country" | "fake-bot" | "bot" | "path" | "password" | "rule-rate" | "rate" | "login-rate" | "waf";

export type IpRule = {
  id: string;
  kind: "allow" | "block" | "only";
  ip: string | null;
  list: { id: string; name: string; entryCount: number; orgId: string } | null;
  note: string | null;
  expiresAt: string | null;
  createdBy: { id: string; name: string } | null;
  createdAt: string;
  country: string | null;
};

export type HeadersSettings = {
  hsts: "off" | "on" | "subdomains" | "preload";
  framing: "sameorigin" | "deny" | "origins";
  frameOrigins: string[];
  referrer: "no-referrer" | "same-origin" | "strict-origin" | "strict-origin-when-cross-origin";
  denyFeatures: ("camera" | "microphone" | "geolocation" | "payment" | "usb")[];
  csp: { mode: "report" | "enforce"; policy: string } | null;
};

export type You = {
  address: string | null;
  country: string | null;
  site: "allowed" | "blocked" | "unknown";
  login: "allowed" | "blocked" | "password" | "unknown";
  reason: string | null;
};

export type ServerAccess = {
  allow: IpRule[];
  block: IpRule[];
  only: { scope: SecurityScope; rules: IpRule[] } | null;
  countries: { mode: "block" | "allowOnly"; scope: SecurityScope; countries: string[]; continents: string[] } | null;
  paths: { path: string; ips: string[]; listIds: string[]; password: { user: string } | null }[];
  behindCloudflare: "auto" | "yes" | "no";
  realIp: "cloudflare" | "direct";
};

/** Who can use WordPress's application passwords. */
export type AppPasswords = "everyone" | "admins" | "nobody";
/** The login part, which is WordPress's (the Security menu's WordPress part). */
export type LoginSettings = {
  ratePerMinute: number | null;
  extraPassword: { user: string; password?: string } | null;
  applicationPasswords: AppPasswords;
  recordLogins: boolean;
  xmlrpc: boolean;
  fileEditing: boolean;
  /** User names and the WordPress version kept from visitors. */
  hideUsers: boolean;
  hideVersion: boolean;
  /** The login page's own address ("team-login"); null: wp-login.php. */
  loginPath: string | null;
  /** A plain note from the API, like login bans not applying behind Cloudflare's proxy. Read-only. */
  notice?: string | null;
};
/** What the server has in force of WordPress's own options, and what keeps one from changing (docs/api.md). */
export type WordPressState = {
  /** The login page's address in force ("team-login"); null: wp-login.php. */
  loginPathApplied: string | null;
  /** The login page's full address in force. */
  loginUrl: string | null;
  /** "Live's server needs an agent update for this.": user names, the version, admins-only passwords and the address can't change yet. */
  agentUpdate: string | null;
  /** Why the login page can't move here (WordPress straight from a repository), or null. */
  loginPathUnavailable: string | null;
};
/**
 * Bots: the lists, no user agent, the user agents you block (`words`); `exceptions` are list user agents let through,
 * `allow` user agents always let past the bot rules, `fakeCrawlers` blocks bots pretending to be Google or Bing.
 */
export type BotSettings = { attackTools: boolean; aiCrawlers: boolean; seoCrawlers: boolean; emptyUserAgent: boolean; words: string[]; exceptions: string[]; allow: string[]; fakeCrawlers: boolean };
/** Bots' newer options on the server, and what each word turned away in 24 hours (docs/api.md). */
export type BotsState = {
  /** "Live's server needs an agent update for this.": the allow list and fake search engines can't work yet. */
  agentUpdate: string | null;
  fakeCrawlers: { ready: boolean; fetchedAt: string | null; reason: string | null; blocked24h: { google: number | null; bing: number | null } | null };
  /** Lowercased word → requests turned away in 24 hours; null until the server counts them. */
  blocked24h: Record<string, number> | null;
};

export type HttpMethod = "GET" | "HEAD" | "POST" | "PUT" | "PATCH" | "DELETE" | "OPTIONS";
export type RuleHeader = "referer" | "origin" | "accept-language" | "content-type" | "x-requested-with" | "authorization" | "cookie";
export type RuleCondition =
  | { field: "path"; op: "is" | "startsWith" | "contains"; value: string }
  | { field: "method"; op: "in"; methods: HttpMethod[] }
  | { field: "query"; op: "contains"; value: string }
  | { field: "userAgent"; op: "contains"; value: string }
  | { field: "country"; op: "in" | "notIn"; countries: string[]; continents: string[] }
  | { field: "address"; op: "in" | "notIn"; ips: string[]; listIds: string[] }
  | { field: "header"; op: "present" | "missing" | "equals"; header: RuleHeader; value?: string };
/** A rule's action; a password rule's password is sent only when it's set or changed. */
export type RuleAction = { type: "block" } | { type: "allow" } | { type: "limit"; perMinute: number } | { type: "password"; user: string; password?: string };
/** A custom rule (docs/security-controls.md, "Custom rules"). `usable`: it's on and can match; `blocked24h`: null until counted. */
export type CustomRule = { id?: string; name: string; enabled: boolean; conditions: RuleCondition[]; action: RuleAction; usable?: boolean; blocked24h?: number | null };
export type RateLimitSettings = { mode: "off" | "normal" | "strict" | "custom"; perSecond: number | null; burst: number | null };
export type RatePresets = { normal: { perSecond: number; burst: number }; strict: { perSecond: number; burst: number } };
export type FirewallSettings = { mode: "off" | "detect" | "block"; paranoia: 1 | 2; inboundThreshold: 5 | 10 | 20 | 50; exclusions: { ruleId: string; path: string | null }[] };

export type SecurityControls = {
  environmentId: string;
  environment: "live" | "test" | "dev";
  siteId: string;
  siteKind: "wordpress" | "static" | "php";
  canChange: boolean;
  access: ServerAccess;
  login: LoginSettings;
  bots: BotSettings;
  rateLimit: RateLimitSettings & { presets?: RatePresets };
  firewall: FirewallSettings;
  headers: HeadersSettings;
  /** WordPress only (null otherwise; absent from an older API). */
  wordpress?: WordPressState | null;
  /** Custom rules, in order (absent from an older API). */
  rules?: CustomRule[];
  rulesState?: { agentUpdate: string | null };
  botsState?: BotsState;
  limits: { allow: number; block: number; only: number; paths: number; pathIps: number; words: number; exclusions: number; allowWords?: number; rules?: number; ruleConditions?: number; ruleIps?: number };
  applied: { state: "applied" | "applying" | "waiting" | "failed"; version: number; appliedVersion: number | null; operationId: string | null; error: string | null };
  summary: string[];
  you: You;
  /** The security profile it follows (docs/api.md, "Security profiles"); `label` is "Strict, changed here" with overrides. */
  profile?: { id: string; name: string; builtIn: boolean; label: string } | null;
  /** The controls that differ from the profile ("login.ratePerMinute"). */
  overrides?: string[];
  /** Each override in words: "Login limit", "20/min" here, "10/min" in the profile. */
  overrideChanges?: { key: string; label: string; here: string; profile: string }[];
  /** What it runs, in a profile's shape. */
  effective?: ProfileControls;
};

export type ChangeResult = { controls: SecurityControls; operationId: string | null };
export type DryRun = { dryRun: true; you: You; summary: string[] };

export type BotList = { label: string; description: string; userAgents: string[] };
export type BotLists = { attackTools: BotList; aiCrawlers: BotList; seoCrawlers: BotList };

export type MyAddress = { address: string | null; family: 4 | 6 | null; country: string | null; countryName: string | null; reason: string | null };

export type Recommendation = {
  id: string;
  severity: "high" | "medium" | "low";
  title: string;
  detail: string;
  action: { label: string; kind: "apply" } | { label: string; kind: "open"; section: "access" | "logins" | "wordpress" | "bots" | "firewall" | "headers" | "activity" };
};

export type SecurityOverview = {
  summary: string[];
  blocked24h: { total: number; byReason: { reason: BlockReason; count: number }[] } | null;
  recommendations: Recommendation[];
  refreshedAt: string | null;
};

export type RowAction = { kind: "block_ip"; ip: string } | { kind: "allow_ip"; ip: string } | { kind: "allow_rule"; ruleId: string; path: string | null } | { kind: "block_ua"; word: string } | { kind: "allow_ua"; word: string } | null;

export type SecurityActivity = {
  refreshedAt: string | null;
  refreshing: boolean;
  error: string | null;
  window: { since: string; until: string } | null;
  blocked: {
    total: number;
    byReason: { reason: BlockReason; count: number }[];
    /** byReason per hour, to break an hour down; null from agents that don't send it yet. */
    byHour: { at: string; count: number; byReason?: { reason: BlockReason; count: number }[] | null }[];
    topIps: { ip: string; country: string | null; count: number; lastAt: string; action: RowAction }[];
    topCountries: { country: string; count: number }[];
    topPaths: { path: string; count: number }[];
    topUserAgents: { userAgent: string; count: number; action?: RowAction }[];
    samples: { at: string; ip: string; country: string | null; method: string; uri: string; status: number; reason: BlockReason; rule: string | null; userAgent: string; action: RowAction }[];
  } | null;
  logins: { ok: number; failed: number; recent: { at: string; user: string; ip: string; country: string | null; ok: boolean; reason: string | null; action: RowAction }[] } | null;
  truncated: boolean;
};

export type CopyTarget = {
  environmentId: string;
  siteId: string;
  siteName: string;
  environment: "live" | "test" | "dev";
  canChange: boolean;
  reason: string | null;
  changes: { part: Part; lines: string[] }[];
  unchanged: boolean;
};

export type SavedList = {
  id: string;
  orgId: string;
  orgName: string;
  name: string;
  description: string | null;
  entryCount: number;
  editable: boolean;
  usedBy: { siteId: string; siteName: string; environmentId: string; environment: "live" | "test" | "dev"; kinds: ("allow" | "block" | "only" | "path")[] }[];
  updatedAt: string;
  updatedBy: { id: string; name: string } | null;
};
export type ListEntry = { id: string; ip: string; note: string | null; country: string | null; createdAt: string; createdBy: { id: string; name: string } | null };

/**
 * What a security profile sets (docs/api.md, "Security profiles"), and what an environment runs in that shape
 * (`effective`). Single addresses, protected paths, the extra login password, the login page's own address and
 * Cloudflare stay the environment's own.
 */
export type ProfileControls = {
  login: { ratePerMinute: number | null; applicationPasswords: AppPasswords; recordLogins: boolean; xmlrpc: boolean; fileEditing: boolean; hideUsers: boolean; hideVersion: boolean };
  bots: BotSettings;
  rateLimit: RateLimitSettings;
  firewall: FirewallSettings;
  headers: HeadersSettings;
  access: {
    countries: ServerAccess["countries"];
    /** Saved lists always allowed. */
    allowListIds: string[];
    /** Saved lists blocked. */
    blockListIds: string[];
    /** Dev and Test only reachable from these saved lists (never Live). */
    devTestOnly: { scope: SecurityScope; listIds: string[]; addresses?: number } | null;
  };
  /** Custom rules (no passwords or single addresses in a profile). Absent from an older API. */
  rules?: CustomRule[];
};

/** The rate limit presets, for before the API sends its own (`rateLimit.presets`). */
export const RATE_PRESETS: RatePresets = { normal: { perSecond: 10, burst: 40 }, strict: { perSecond: 3, burst: 15 } };

/* ── The editor's own shapes ────────────────────────────────────── */

/** An address, range or saved list on a list, as the editors hold it. */
export type IpEntry = {
  /** The address or range; `list:<id>` for a saved list. */
  value: string;
  note: string | null;
  /** When it stops applying (shown); null: never. */
  expiresAt: string | null;
  /** For a new entry: how long, sent as expiresIn. */
  expiresIn?: ExpiresIn;
  listId?: string | null;
  listName?: string | null;
  listCount?: number | null;
  country?: string | null;
  addedBy?: string | null;
  ruleId?: string | null;
};

export type ProtectedPath = { path: string; onlyIps: string[]; listIds: string[]; password: { user: string; password?: string; set?: boolean } | null };

export type AccessSettings = {
  allow: IpEntry[];
  block: IpEntry[];
  only: { scope: SecurityScope; entries: IpEntry[] } | null;
  countries: { mode: "block" | "allowOnly"; scope: SecurityScope; countries: string[]; continents: string[] } | null;
  paths: ProtectedPath[];
  behindCloudflare: "auto" | "yes" | "no";
};

export function fromRule(r: IpRule): IpEntry {
  return r.list
    ? { value: `list:${r.list.id}`, note: r.note, expiresAt: r.expiresAt, listId: r.list.id, listName: r.list.name, listCount: r.list.entryCount, ruleId: r.id, addedBy: r.createdBy?.name ?? null }
    : { value: r.ip ?? "", note: r.note, expiresAt: r.expiresAt, country: r.country, ruleId: r.id, addedBy: r.createdBy?.name ?? null };
}

export function accessFromServer(a: ServerAccess): AccessSettings {
  return {
    allow: a.allow.map(fromRule),
    block: a.block.map(fromRule),
    only: a.only ? { scope: a.only.scope, entries: a.only.rules.map(fromRule) } : null,
    countries: a.countries,
    paths: a.paths.map((p) => ({ path: p.path, onlyIps: p.ips, listIds: p.listIds, password: p.password ? { user: p.password.user, set: true } : null })),
    behindCloudflare: a.behindCloudflare,
  };
}

type Entry = string | { ip: string; note?: string | null; expiresIn?: ExpiresIn } | { listId: string; note?: string | null };
const toEntry = (e: IpEntry): Entry =>
  e.listId ? { listId: e.listId, note: e.note } : e.ruleId ? { ip: e.value, note: e.note } : { ip: e.value, note: e.note, ...(e.expiresIn !== undefined ? { expiresIn: e.expiresIn } : {}) };

/** Only what changed, as the API's AccessPatch: a list that's given replaces that list. */
export function accessPatch(before: AccessSettings, after: AccessSettings): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
  if (!same(before.allow, after.allow)) p.allow = after.allow.map(toEntry);
  if (!same(before.block, after.block)) p.block = after.block.map(toEntry);
  if (!same(before.only, after.only)) p.only = after.only ? { scope: after.only.scope, entries: after.only.entries.map(toEntry) } : null;
  if (!same(before.countries, after.countries)) p.countries = after.countries;
  if (!same(before.paths, after.paths))
    p.paths = after.paths.map((x) => ({
      path: x.path,
      ips: x.onlyIps,
      listIds: x.listIds,
      // Left out keeps the password that's set.
      ...(x.password ? (x.password.password ? { password: { user: x.password.user, password: x.password.password } } : x.password.set ? {} : { password: { user: x.password.user } }) : { password: null }),
    }));
  if (before.behindCloudflare !== after.behindCloudflare) p.behindCloudflare = after.behindCloudflare;
  return p;
}

/** Only what changed in a flat part (login, bots, rate limit, firewall, headers). */
export function flatPatch<T extends object>(before: T, after: T): Partial<T> {
  const out: Partial<T> = {};
  for (const k of Object.keys(after) as (keyof T)[]) if (JSON.stringify(before[k]) !== JSON.stringify(after[k])) out[k] = after[k];
  return out;
}

/* ── Routes ─────────────────────────────────────────────────────── */

const envBase = (o: string, s: string, e: string) => `/orgs/${o}/sites/${s}/environments/${e}/security`;
const PART_PATH: Record<Part, string> = { access: "access", login: "login", bots: "bots", rateLimit: "rate-limit", firewall: "firewall", headers: "headers", rules: "rules" };

/** Whether an error means "this isn't in the API yet". */
export const notYet = (e: unknown) => isApiError(e) && (e.status === 404 || e.status === 405 || e.status === 501);
const retry = (n: number, e: unknown) => !notYet(e) && n < 2;

export const ctlKey = (o: string, s: string, e: string) => [...keys.site(o, s), "security-controls", e] as const;

export function useSecurityControls(siteId: string, envId: string) {
  const o = useOrg();
  return useQuery({
    queryKey: ctlKey(o.id, siteId, envId),
    queryFn: ({ signal }) => api.get<SecurityControls>(`${envBase(o.id, siteId, envId)}/controls`, signal),
    retry,
    staleTime: 10_000,
  });
}

export function useSecurityOverview(siteId: string, envId: string) {
  const o = useOrg();
  return useQuery({
    queryKey: [...ctlKey(o.id, siteId, envId), "overview"],
    queryFn: ({ signal }) => api.get<SecurityOverview>(`${envBase(o.id, siteId, envId)}/overview`, signal),
    retry,
    refetchInterval: 60_000,
  });
}

export function useSecurityActivity(siteId: string, envId: string) {
  const o = useOrg();
  return useQuery({
    queryKey: [...ctlKey(o.id, siteId, envId), "activity"],
    queryFn: ({ signal }) => api.get<SecurityActivity>(`${envBase(o.id, siteId, envId)}/activity`, signal),
    retry,
    refetchInterval: (q) => (q.state.data?.refreshing ? 5_000 : 60_000),
  });
}

export function useBotLists() {
  return useQuery({ queryKey: ["security", "bots"], queryFn: ({ signal }) => api.get<{ lists: BotLists }>("/security/bots", signal), select: (d) => d.lists, retry, staleTime: 3_600_000 });
}

export function useMyAddress() {
  return useQuery({ queryKey: ["me", "address"], queryFn: ({ signal }) => api.get<MyAddress>("/me/address", signal), retry, staleTime: 300_000 });
}

function useInvalidateSite(siteId: string) {
  const o = useOrg();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.site(o.id, siteId) });
    void qc.invalidateQueries({ queryKey: keys.operationLists(o.id) });
  };
}

/** Change one part. With `dryRun`, nothing is saved: the answer is what would change and what it does to your address. */
export function useChangePart(siteId: string, envId: string) {
  const o = useOrg();
  const done = useInvalidateSite(siteId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { part: Part; patch: object; dryRun?: boolean; label?: string }) =>
      api.patch<ChangeResult | DryRun>(`${envBase(o.id, siteId, envId)}/${PART_PATH[v.part]}`, { ...v.patch, ...(v.dryRun ? { dryRun: true } : {}) }),
    onSuccess: (r, v) => {
      if ("dryRun" in r) return;
      qc.setQueryData(ctlKey(o.id, siteId, envId), r.controls);
      if (r.operationId) watchOperation(r.operationId, { title: `${v.label ?? "Security rules"} applied`, description: "The new rules are live." });
      done();
    },
  });
}

/** One-click "Block this address" and "Allow this address". */
export function useAddRule(siteId: string, envId: string) {
  const o = useOrg();
  const done = useInvalidateSite(siteId);
  return useMutation({
    mutationFn: (v: { kind: "allow" | "block" | "only"; ip?: string; listId?: string; note?: string; expiresIn?: ExpiresIn }) =>
      api.post<ChangeResult & { rule: IpRule }>(`${envBase(o.id, siteId, envId)}/rules`, v),
    onSuccess: (r, v) => {
      if (r.operationId) watchOperation(r.operationId, { title: v.kind === "block" ? `${v.ip ?? "The list"} is blocked` : `${v.ip ?? "The list"} is allowed` });
      done();
    },
  });
}

/** "Block this user agent" and "Allow this user agent", from Activity and Traffic: the word moves between the lists. */
export function useAddUserAgent(siteId: string, envId: string) {
  const o = useOrg();
  const done = useInvalidateSite(siteId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { action: "block" | "allow"; userAgent: string }) => api.post<ChangeResult>(`${envBase(o.id, siteId, envId)}/bots/user-agents`, v),
    onSuccess: (r, v) => {
      qc.setQueryData(ctlKey(o.id, siteId, envId), r.controls);
      if (r.operationId) watchOperation(r.operationId, { title: v.action === "block" ? `User agents with "${v.userAgent}" are blocked` : `User agents with "${v.userAgent}" are let through` });
      done();
    },
  });
}

/** "Allow this rule here", from a firewall block. */
export function useAddExclusion(siteId: string, envId: string) {
  const o = useOrg();
  const done = useInvalidateSite(siteId);
  return useMutation({
    mutationFn: (v: { ruleId: string; path?: string | null }) => api.post<ChangeResult>(`${envBase(o.id, siteId, envId)}/firewall/exclusions`, v),
    onSuccess: (r, v) => {
      if (r.operationId) watchOperation(r.operationId, { title: `Rule ${v.ruleId} won't block ${v.path ?? "here"} any more` });
      done();
    },
  });
}

export function useRefreshActivity(siteId: string, envId: string) {
  const o = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ operationId: string | null }>(`${envBase(o.id, siteId, envId)}/activity/refresh`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...ctlKey(o.id, siteId, envId), "activity"] }),
  });
}

export function useRecommendation(siteId: string, envId: string) {
  const o = useOrg();
  const done = useInvalidateSite(siteId);
  return useMutation({
    mutationFn: (v: { id: string; act: "apply" | "dismiss"; title: string }) => api.post<ChangeResult | { ok: true }>(`${envBase(o.id, siteId, envId)}/recommendations/${v.id}/${v.act}`, {}),
    onSuccess: (r, v) => {
      if ("operationId" in r && r.operationId) watchOperation(r.operationId, { title: v.title });
      done();
    },
  });
}

export function useCopyPreview(siteId: string, envId: string, parts: Part[], targets: string[], enabled: boolean) {
  const o = useOrg();
  return useQuery({
    queryKey: [...ctlKey(o.id, siteId, envId), "copy-preview", parts.join(","), targets.join(",")],
    queryFn: () => api.post<{ targets: CopyTarget[] }>(`${envBase(o.id, siteId, envId)}/copy/preview`, { parts, targets }),
    enabled: enabled && targets.length > 0,
    retry,
  });
}

export function useCopy(siteId: string, envId: string) {
  const o = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { parts: Part[]; targets: string[] }) => api.post<{ targets: (CopyTarget & { operationId: string | null; error: string | null })[] }>(`${envBase(o.id, siteId, envId)}/copy`, v),
    onSuccess: (r) => {
      for (const t of r.targets) if (t.operationId) watchOperation(t.operationId, { title: `Rules copied to ${t.siteName}'s ${t.environment === "live" ? "Live" : t.environment === "test" ? "Test" : "Dev"}` });
      void qc.invalidateQueries({ queryKey: ["org", o.id] });
    },
  });
}

/* ── The org's saved lists ───────────────────────────────────── */

export function useSavedLists(enabled = true) {
  const o = useOrg();
  return useQuery({
    queryKey: ["org", o.id, "security-lists"],
    queryFn: ({ signal }) => api.get<{ lists: SavedList[] }>(`/orgs/${o.id}/security/lists`, signal),
    select: (d) => d.lists,
    retry,
    enabled,
  });
}

export function useSavedList(id: string | null) {
  const o = useOrg();
  return useQuery({
    queryKey: ["org", o.id, "security-lists", id],
    queryFn: ({ signal }) => api.get<{ list: SavedList & { entries: ListEntry[] } }>(`/orgs/${o.id}/security/lists/${id}`, signal),
    select: (d) => d.list,
    enabled: !!id,
    retry,
  });
}

export function useSaveList() {
  const o = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: string; name: string; description?: string | null; entries: { ip: string; note?: string | null }[] }) =>
      v.id
        ? api.patch<{ list: SavedList; applied: { environmentId: string; operationId: string }[] }>(`/orgs/${o.id}/security/lists/${v.id}`, { name: v.name, description: v.description, entries: v.entries })
        : api.post<{ list: SavedList }>(`/orgs/${o.id}/security/lists`, { name: v.name, description: v.description, entries: v.entries }),
    onSuccess: (r) => {
      for (const a of (r as { applied?: { operationId: string }[] }).applied ?? []) watchOperation(a.operationId);
      void qc.invalidateQueries({ queryKey: ["org", o.id] });
    },
  });
}

export function useDeleteList() {
  const o = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/orgs/${o.id}/security/lists/${id}`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["org", o.id] }),
  });
}

/* ── A server's network blocks and SSH ──────────────────────────── */

export type ServerRule = { id: string; ip: string; note: string | null; expiresAt: string | null; createdBy: { id: string; name: string } | null; createdAt: string; country: string | null };

export function useServerBlock(serverId: string) {
  const o = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { ip: string; note?: string | null }) => api.post<{ rule: ServerRule; operationId: string }>(`/orgs/${o.id}/servers/${serverId}/blocks`, v),
    onSuccess: (r, v) => {
      watchOperation(r.operationId, { title: `${v.ip} is blocked on every port` });
      void qc.invalidateQueries({ queryKey: keys.server(o.id, serverId) });
    },
  });
}

export function useServerBlocked(serverId: string) {
  const o = useOrg();
  return useQuery({
    queryKey: [...keys.server(o.id, serverId), "blocked"],
    queryFn: ({ signal }) =>
      api.get<{
        total: number;
        byReason: { reason: BlockReason; count: number }[];
        topIps: { ip: string; country: string | null; count: number }[];
        topCountries: { country: string; count: number }[];
        environments: { environmentId: string; siteId: string; siteName: string; environment: "live" | "test" | "dev"; total: number; refreshedAt: string | null }[];
        refreshedAt: string | null;
      }>(`/orgs/${o.id}/servers/${serverId}/security/blocked`, signal),
    retry,
  });
}

/** A server firewall change, or its dry run: what it would do, and to your own address. */
export type ServerFirewallDryRun = {
  dryRun: true;
  summary: string[];
  you: { address: string | null; country: string | null; blocked: boolean | null; ssh: "open" | "allowed" | "blocked" | "unknown"; reason: string | null };
};

export function useServerFirewallChange(serverId: string) {
  const o = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { body: object; dryRun?: boolean }) => api.patch<ServerFirewallDryRun | { firewall: unknown; operationId: string | null }>(`/orgs/${o.id}/servers/${serverId}/firewall`, { ...v.body, ...(v.dryRun ? { dryRun: true } : {}) }),
    onSuccess: (r) => {
      if ("dryRun" in r) return;
      if (r.operationId) watchOperation(r.operationId, { title: "Server firewall applied" });
      void qc.invalidateQueries({ queryKey: keys.server(o.id, serverId) });
      void qc.invalidateQueries({ queryKey: keys.operationLists(o.id) });
    },
  });
}
